import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useReducer,
} from 'react';
import config from 'config';
import { useAuthN, useAuthZ } from 'hooks/auth/subscriptions';
import { isUpcoming as isUpcomingHelper } from 'support/videoHelper';
import { KitcenSinkRowItemDataItem, RelatedItem } from '../store-types';
import { Provider } from '../app-types';

export interface VideoValidationError {
  type: 'upcoming' | 'authN' | 'authZ';
  providers?: Provider[];
  isCarousel?: boolean;
}

export const useVideoValidation = (
  video: KitcenSinkRowItemDataItem | RelatedItem | null | undefined = null,
  onError: (error: unknown | VideoValidationError) => void,
  isCarousel: boolean,
) => {
  // Falling back to empty array to short-circuit around external
  // api calls pertaining to auth
  const authTypes = video?.authTypes || [];
  const isAuthenticated = useAuthN(authTypes);
  const [isAuthorized, authorizedProvider, loading, error] = useAuthZ(video);
  const isUpcoming = isUpcomingHelper(video);

  const latestOnError = useRef(onError);
  useEffect(() => {
    latestOnError.current = onError;
  });
  useEffect(() => {
    if (isUpcoming && isAuthenticated) {
      latestOnError.current({ type: 'upcoming' });
    } else if (!isAuthenticated) {
      latestOnError.current({
        type: 'authN',
        providers: authTypes,
        isCarousel,
      });
    } else if (error) {
      latestOnError.current({ type: 'authZ' });
    }
  });

  return {
    isValid:
      video && !isUpcoming && isAuthenticated && isAuthorized && !loading,
    authorizedProvider: authorizedProvider ?? undefined,
    loading,
  };
};

export const usePlayerOverlay = (initialValue = false) => {
  const [isPlayerOverlayVisible, setIsPlayerOverlayVisible] = useState(
    initialValue,
  );
  const [keepPlayerOverlay, setKeepPlayerOverlay] = useState(false);
  // Necessary to have ignition refresh the timer
  const [togglePlayer, setTogglePlayer] = useState(false);

  const igniteOverlay = useCallback(() => {
    setIsPlayerOverlayVisible(true);
    setTogglePlayer(val => !val);
  }, []);

  const hideOverlay = useCallback(() => {
    setIsPlayerOverlayVisible(false);
  }, []);

  const delay =
    (config && config.playback && config.playback.overlayTimeout) || 5000;

  useEffect(() => {
    if (isPlayerOverlayVisible && !keepPlayerOverlay) {
      const timeout = setTimeout(() => {
        setIsPlayerOverlayVisible(false);
      }, delay);

      return () => {
        clearTimeout(timeout);
      };
    }

    return undefined;
  }, [isPlayerOverlayVisible, keepPlayerOverlay, togglePlayer, delay]);

  const enableAutoHide = useCallback((val: boolean) => {
    if (!val) setIsPlayerOverlayVisible(true);

    setKeepPlayerOverlay(!val);
  }, []);

  return { isPlayerOverlayVisible, igniteOverlay, enableAutoHide, hideOverlay };
};

export const useDiscoveryRowOverlay = (initialValue = false) => {
  const [isOverlayDiscoveryVisible, setIsOverlayVisible] = useState(
    initialValue,
  );

  const [keepDiscoveryOverlay, setKeepDiscoveryOverlay] = useState(false);
  // Necessary to have ignition refresh the timer
  const [toggleDiscovery, setToggleDiscovery] = useState(false);

  const igniteDiscoveryOverlay = useCallback(() => {
    setIsOverlayVisible(true);
    setToggleDiscovery(val => !val);
  }, []);

  const hideDiscoveryOverlay = useCallback(() => {
    setIsOverlayVisible(false);
  }, []);

  const delay =
    (config && config.playback && config.playback.overlayTimeout) || 5000;

  useEffect(() => {
    if (isOverlayDiscoveryVisible && !keepDiscoveryOverlay) {
      const timeout = setTimeout(() => {
        setIsOverlayVisible(false);
      }, delay);

      return () => {
        clearTimeout(timeout);
      };
    }

    return undefined;
  }, [isOverlayDiscoveryVisible, keepDiscoveryOverlay, toggleDiscovery, delay]);

  const enableAutoHideDiscovery = useCallback((val: boolean) => {
    if (!val) setIsOverlayVisible(true);

    setKeepDiscoveryOverlay(!val);
  }, []);

  return {
    isOverlayDiscoveryVisible,
    igniteDiscoveryOverlay,
    hideDiscoveryOverlay,
    enableAutoHideDiscovery,
  };
};

type FFRWAction = { type: 'FF' } | { type: 'RW' } | { type: 'SEEK' };

export const useFFRW = (
  progress: number,
  duration: number,
  handleSeek: (time: number) => void,
  onPlaybackRateChange: (rate: number) => void,
) => {
  const playbackRates = useMemo(() => [-16, -8, -4, -2, 1, 2, 4, 8, 16], []);
  const labels = useMemo(
    () => ['-16', '-8', '-4', '-2', null, '2', '4', '8', '16'],
    [],
  );

  const [playbackRate, dispatch] = useReducer((prevRate: number, action: FFRWAction) => {
    const currentIndex = playbackRates.findIndex(item => item === prevRate);
    switch (action.type) {
      case 'FF':
        return currentIndex + 1 < playbackRates.length
          ? playbackRates[currentIndex + 1]
          : prevRate;
      case 'RW':
        return currentIndex - 1 >= 0
          ? playbackRates[currentIndex - 1]
          : prevRate;
      case 'SEEK':
        return 1;
      default:
        return prevRate;
    }
  }, 1);

  const label = useMemo(() => {
    const index = playbackRates.findIndex(item => item === playbackRate);
    return labels[index] || null;
  }, [playbackRate, playbackRates, labels]);

  const ff = useCallback(() => {
    dispatch({ type: 'FF' });
  }, []);

  const rw = useCallback(() => {
    dispatch({ type: 'RW' });
  }, []);

  const seek = useCallback(() => {
    dispatch({ type: 'SEEK' });
  }, []);

  const [seekTime, setSeekTime] = useState<number | null>(null);

  const jumpForward = useCallback(() => {
    setSeekTime(val => {
      const initialTime = val !== null ? val : progress;
      if (initialTime + 10 < duration) {
        return initialTime + 10;
      }
      return duration;
    });
  }, [progress, duration]);

  const jumpBack = useCallback(() => {
    setSeekTime(val => {
      const initialTime = val !== null ? val : progress;
      if (initialTime - 10 > 0) {
        return initialTime - 10;
      }
      return 0;
    });
  }, [progress]);

  const savedHandleSeek = useRef<((time: number) => void) | null>(null);
  useEffect(() => {
    savedHandleSeek.current = handleSeek;
  }, [handleSeek]);
  useEffect(() => {
    if (playbackRate === 1 && seekTime !== null) {
      const debouncer = setTimeout(() => {
        savedHandleSeek.current?.(seekTime);
        setSeekTime(null);
      }, 500);

      return () => {
        clearTimeout(debouncer);
      };
    }

    return undefined;
  }, [playbackRate, seekTime]);

  const onPlaybackRateChangeSaved = useRef<((rate: number) => void) | null>(null);
  useEffect(() => {
    onPlaybackRateChangeSaved.current = onPlaybackRateChange;
  }, [onPlaybackRateChange]);
  useEffect(() => {
    onPlaybackRateChangeSaved.current?.(playbackRate);
  }, [playbackRate]);

  const latestProgress = useRef<number | null>(null);
  const latestDuration = useRef<number | null>(null);
  useEffect(() => {
    latestProgress.current = progress;
  }, [progress]);
  useEffect(() => {
    latestDuration.current = duration;
  }, [duration]);
  useEffect(() => {
    if (playbackRate !== 1) {
      try {
        setSeekTime(val => (!val ? latestProgress.current : val));
        const interval = setInterval(() => {
          setSeekTime(val => {
            if ((val as number) + playbackRate <= 0) {
              return 0;
            }
            if ((val as number) + playbackRate >= (latestDuration.current as number)) {
              return latestDuration.current;
            }

            return (val as number) + playbackRate;
          });
        }, 1000);

        return () => {
          clearInterval(interval);
        };
      } catch {
        // no-op
      }
    }

    return undefined;
  }, [playbackRate]);

  return {
    label,
    playbackRate,
    seekTime,
    ff,
    rw,
    jumpForward,
    jumpBack,
    seek,
  };
};
